import { z } from 'zod'
import { resultEnvelope } from './envelope'
import type { ApiClient } from './client'

/**
 * API tokens — POST /{clusterId}/api/tokens/{list,generate,delete}-token
 *
 * The machine identity for a cluster: an app name, a scope, a capability set,
 * and a secret shown exactly once. Contract read off the legacy service and
 * screen (`services/token_apis.js`, `pages/admin/tokenManagement`), with the
 * list path confirmed by the live navigation sweep
 * (docs/inventory/api-observed.json, `gotham/system/token`).
 *
 * Facts this design leans on:
 *
 * - All three are POST, including the read. Nothing here retries by default.
 * - The list uses the cluster-scoped mgmt envelope, so `count` is the total
 *   across ALL pages and `data.result` is only this page (see envelope.ts).
 * - `token_id` is the delete key and the row's only stable identity. The
 *   legacy screen's `#` column was page arithmetic computed client-side, not
 *   a field — it must not be used as a React key.
 * - `scope_id` travels ON the row. A token may live in a scope other than the
 *   one being viewed, so delete takes the row's scope, never the route's.
 * - `generate-token` returns the secret in `data` as a bare string. No list
 *   field carries it, and no endpoint re-reads it: lose it and the only
 *   recovery is issuing another token.
 * - `subject` is sent only when `capabilities` includes 'case_mgmt', where it
 *   is a JSON string (e.g. `{"config_id": "…"}`), not an object.
 *
 * Fields the screen has no source for, and which must not be synthesised:
 * created-at, last-used-at, expiry. A token row carries none of the three
 * (docs/settings-requirements.md §5, G2).
 */

export const apiToken = z
  .object({
    /** Stable identity, and the delete key. */
    token_id: z.string(),
    app_name: z.string().default(''),
    description: z.string().nullable().default(null),
    scope_id: z.string().default(''),
    capabilities: z.array(z.string()).default([]),
  })
  .passthrough()

export type ApiToken = z.output<typeof apiToken>

/** `count` is the total across all pages, not `result.length`. */
export const tokensResponse = resultEnvelope(apiToken)

/**
 * The generate response. The legacy screen read `response.data.data` and put
 * it straight into a copy box, so the secret is a bare string at `data` — not
 * the `{ result: [] }` shape the list uses.
 */
export const tokenSecretResponse = z.object({
  status: z.string().optional(),
  message: z.string().optional(),
  data: z.string(),
})

/** The legacy server-side filter shape. Only `app_name` is ever filtered on. */
export interface TokenFilter {
  filter_key: string
  filter_condition: 'AND' | 'OR'
  filter_value: string
  filter_operand: 'LIKE' | '='
}

export interface TokensPage {
  /** This page of tokens. */
  tokens: ApiToken[]
  /** Total matching tokens across all pages. */
  total: number
}

export interface TokensQuery {
  clusterId: string
  scopeId: string
  /** 1-based. */
  pageNo?: number
  pageSize?: number
  /** Substring match on app name; empty means unfiltered. */
  search?: string
}

export interface TokenCreateInput {
  clusterId: string
  scopeId: string
  appName: string
  description: string
  capabilities: string[]
  /** JSON string. Sent only when capabilities include 'case_mgmt'. */
  subject?: string
}

export interface TokenDeleteInput {
  clusterId: string
  /** The scope on the ROW, not the route. */
  scopeId: string
  tokenId: string
}

export interface TokensApi {
  list: (q: TokensQuery) => Promise<TokensPage>
  /** Resolves to the secret, which is returned once and never again. */
  create: (input: TokenCreateInput) => Promise<string>
  remove: (input: TokenDeleteInput) => Promise<void>
}

/** The one filter the legacy screen built, kept in its exact wire shape. */
export function appNameFilter(search: string): TokenFilter[] {
  const value = search.trim()
  if (!value) return []
  return [
    {
      filter_key: 'app_name',
      filter_condition: 'AND',
      filter_value: value,
      filter_operand: 'LIKE',
    },
  ]
}

export function createTokensApi(client: ApiClient): TokensApi {
  return {
    list: async ({ clusterId, scopeId, pageNo = 1, pageSize = 20, search = '' }) => {
      const filters = appNameFilter(search)
      const res = await client.request('POST', `/${clusterId}/api/tokens/list-token`, {
        body: {
          pageno: pageNo,
          pagesize: pageSize,
          scope_id: scopeId,
          ...(filters.length ? { addfilters: filters } : {}),
        },
        schema: tokensResponse,
      })
      return { tokens: res.data.result, total: res.count }
    },

    create: async ({ clusterId, scopeId, appName, description, capabilities, subject }) => {
      const res = await client.request('POST', `/${clusterId}/api/tokens/generate-token`, {
        body: {
          app_name: appName.trim(),
          scope_id: scopeId,
          description: description.trim(),
          capabilities,
          // Only carried for case_mgmt, and only when actually supplied — the
          // legacy screen omits the key entirely otherwise.
          ...(capabilities.includes('case_mgmt') && subject ? { subject: subject.trim() } : {}),
        },
        schema: tokenSecretResponse,
      })
      return res.data
    },

    remove: async ({ clusterId, scopeId, tokenId }) => {
      await client.request('POST', `/${clusterId}/api/tokens/delete-token`, {
        body: { scope_id: scopeId, token_id: tokenId },
        schema: z.object({ status: z.string().optional(), message: z.string().optional() }).passthrough(),
      })
    },
  }
}
